/**
 * FaceEnrollment — first-run enrolment, before anyone can clock in.
 * @file src/web/features/clock/components/FaceEnrollment.tsx
 *
 * @llm-rule WHEN: The signed-in user has no enrolled face yet
 * @llm-rule NOTE: Averages several frames into one descriptor so day-to-day
 *   lighting changes don't cause false rejections. See faceEngine.averageDescriptors.
 */

import { useRef, useState } from 'react';
import { Button, toast } from '@bloomneo/uikit';
import { ShieldCheck } from 'lucide-react';
import { FaceScanner, type FaceScannerHandle } from '../../face/components/FaceScanner';
import { averageDescriptors, FaceCaptureError } from '../../face/lib/faceEngine';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import { Panel } from '../../../shared/components/presence';

/** Frames averaged into the enrolment centroid. */
const SAMPLES = 5;
/** Pause between frames so the samples aren't near-identical. */
const SAMPLE_GAP_MS = 350;

export function FaceEnrollment({ onEnrolled }: { onEnrolled: () => void }) {
  const scannerRef = useRef<FaceScannerHandle>(null);
  const { request } = useAppRequest();

  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  async function enroll() {
    setBusy(true);
    setHint(null);
    setProgress(0);

    const samples: number[][] = [];

    try {
      // The FIRST frame also yields the reference photo. It is taken before the
      // person has started shifting for the remaining samples, so it is the
      // squarest-on shot of the set — the right one to keep on file.
      let reference: string | undefined;

      for (let i = 0; i < SAMPLES; i++) {
        try {
          const result =
            i === 0
              ? await scannerRef.current!.captureWithImage()
              : await scannerRef.current!.capture();
          if (i === 0) reference = result.image;
          samples.push(result.descriptor);
          setProgress(samples.length);
        } catch (err) {
          // Coach and abort — a partial enrolment is worse than none.
          setHint(err instanceof FaceCaptureError ? err.message : 'Capture failed');
          setBusy(false);
          setProgress(0);
          return;
        }

        if (i < SAMPLES - 1) {
          await new Promise((r) => setTimeout(r, SAMPLE_GAP_MS));
        }
      }

      const descriptor = averageDescriptors(samples);
      const result = await request('/api/attendance/face/enroll', {
        method: 'POST',
        body: { faceDescriptor: descriptor, faceImage: reference },
      });

      if (!result.ok) {
        toast.error(result.error);
        return;
      }

      toast.success('Face enrolled — you can clock in now');
      onEnrolled();
    } finally {
      setBusy(false);
      setProgress(0);
    }
  }

  return (
    <Panel padded={false} className="overflow-hidden">
      <div className="bg-gradient-to-b from-paper-sunk to-paper px-6 pb-5 pt-6">
        <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink-faint">
          One-time setup
        </div>
        <h2 className="mt-2.5 text-[24px] font-semibold leading-tight tracking-tight text-ink">
          Enrol your face.
        </h2>
        <p className="mt-2 text-[13.5px] leading-relaxed text-ink-soft">
          We’ll take {SAMPLES} readings and average them, and keep one photo as your reference.
          Every clock-in from now on is matched against it — so get this one right. You can only
          enrol once; after that a Company Admin or Head has to reset it for you.
        </p>
      </div>

      <div className="flex flex-col gap-3.5 px-6 pb-6 pt-4">
        <FaceScanner
          ref={scannerRef}
          caption={busy ? `reading ${progress} of ${SAMPLES}…` : 'centre your face in the oval'}
        />

        {/* Sample progress */}
        {busy && (
          <div className="flex justify-center gap-1.5">
            {Array.from({ length: SAMPLES }, (_, i) => (
              <span
                key={i}
                className="h-1.5 w-8 rounded-full transition-colors"
                style={{ background: i < progress ? 'var(--brand)' : 'var(--rule)' }}
              />
            ))}
          </div>
        )}

        {hint && (
          <p className="rounded-xl bg-[var(--state-warn-bg)] px-3.5 py-2.5 text-[12.5px] leading-relaxed text-ink">
            {hint}
          </p>
        )}

        <Button
          className="h-12 rounded-[14px] bg-brand text-[15px] font-semibold text-brand-ink hover:bg-brand/90"
          disabled={busy}
          onClick={() => void enroll()}
        >
          {busy ? `Reading ${progress}/${SAMPLES}…` : 'Enrol my face'}
        </Button>

        <div className="flex items-start gap-2 border-t border-rule pt-3 text-[11.5px] leading-relaxed text-ink-ghost">
          <ShieldCheck className="mt-0.5 size-3.5 flex-none" />
          <p>
            This one photo is kept as your reference, and your Company Admin and Heads can see
            it. Clock-ins are matched against it and send no picture of their own — only a
            mathematical signature, which cannot be turned back into an image.
          </p>
        </div>
      </div>
    </Panel>
  );
}
